import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Outlet, useNavigate, useLocation } from "react-router-dom";
import { startForegroundPushListener, PUSH_REVIEW_EVENT } from "./lib/push";
import { AuthProvider, useAuth } from "./store/auth";
import { AuthFlow } from "./components/AuthFlow";
import { CartProvider } from "./store/cart";
import { ShortlistProvider } from "./store/shortlist";
import { BranchProvider } from "./store/branch";
import { FulfillmentProvider } from "./store/fulfillment";
import { TableProvider, useTable } from "./store/table";
import { GroupCartProvider } from "./store/group-cart";
import { PhoneShell } from "./components/PhoneShell";
import { AnalyticsTracker } from "./components/AnalyticsTracker";
import { AppErrorBoundary } from "./components/AppErrorBoundary";
import { FloatingSkinSelector } from "./components/FloatingSkinSelector";
import { AnalyticsConsentBanner } from "./components/AnalyticsConsentBanner";
import { ChallengeRewardSyncer } from "./components/loyalty/ChallengeRewardSyncer";
import { PwaUpdatePrompt } from "./components/PwaUpdatePrompt";
import { ReviewPromptModal } from "./components/ReviewPromptModal";
import { useReviewPromptTrigger } from "./hooks/useReviewPromptTrigger";
import { HomeScreen } from "./screens/HomeScreen";
import { ShortlistScreen } from "./screens/ShortlistScreen";
import { RequireOrdering, RequireMenuOnly } from "./components/RequireOrdering";
import { useAppMode } from "./hooks/useAppMode";
import { PreferenceCenterScreen } from "./screens/PreferenceCenterScreen";
import { ItemDetailScreen } from "./screens/ItemDetailScreen";
import { CartScreen } from "./screens/CartScreen";
import { CheckoutScreen } from "./screens/CheckoutScreen";
import { SuccessScreen } from "./screens/SuccessScreen";
import { TrackingScreen } from "./screens/TrackingScreen";
import { HistoryScreen } from "./screens/HistoryScreen";
import { OrderDetailScreen } from "./screens/OrderDetailScreen";
import { ReviewSubmitScreen } from "./screens/ReviewSubmitScreen";
import { ProfileScreen } from "./screens/ProfileScreen";
import { ChatScreen } from "./screens/ChatScreen";
import { EditProfileScreen } from "./screens/EditProfileScreen";
import { SavedAddressesScreen } from "./screens/SavedAddressesScreen";
import { SavedPaymentsScreen } from "./screens/SavedPaymentsScreen";
import { NewAddressScreen } from "./screens/NewAddressScreen";
import { NewPaymentScreen } from "./screens/NewPaymentScreen";
import { MapPickerScreen } from "./screens/MapPickerScreen";
import { PinLocationScreen } from "./screens/PinLocationScreen";
import { LoyaltyWalletScreen } from "./screens/LoyaltyWalletScreen";
import { ChallengesScreen } from "./screens/ChallengesScreen";
import { BadgesScreen } from "./screens/BadgesScreen";
import { ReferralsScreen } from "./screens/ReferralsScreen";
import { WalletPassScreen } from "./screens/WalletPassScreen";
import { OffersInboxScreen } from "./screens/OffersInboxScreen";
import { PointsHistoryScreen } from "./screens/PointsHistoryScreen";
import { DineInOrderScreen } from "./screens/DineInOrderScreen";
import { GroupJoinScreen } from "./screens/GroupJoinScreen";
import { SplitPayScreen } from "./screens/SplitPayScreen";
import { CashFollowUpScreen } from "./screens/CashFollowUpScreen";
import { PublicDeliveryTrackingScreen } from "./screens/PublicDeliveryTrackingScreen";
import { resolveSlug } from "./lib/resolveSlug";
import { MerchantUnavailable } from "./components/MerchantUnavailable";
import { useBranch } from "./store/branch";
import { storefrontName } from "@/lib/storefrontName";

/**
 * Layout route: BranchProvider must live inside the Router so it can pull
 * the resolved slug. CartProvider depends on BranchProvider so it sits
 * underneath. PhoneShell renders the bottom nav etc.
 */
function BranchLayout() {
  return (
    <BranchProvider>
      <FulfillmentProvider>
        <CartProvider>
          {/* Mounted unconditionally: inert on ordering links. Mounting it
              conditionally would make a provider appear mid-session as the
              config resolves, remounting everything under it. */}
          <ShortlistProvider>
            <AnalyticsTracker />
            <PushForegroundListener />
            <DineInParamsInjector />
            <AppErrorBoundary>
              <PhoneShell>
                <Outlet />
              </PhoneShell>
            </AppErrorBoundary>
            <GlobalAuthPrompt />
            <ReviewPromptGate />
            <FloatingSkinSelector />
            <AnalyticsConsentBanner />
            <ChallengeRewardSyncer />
          </ShortlistProvider>
        </CartProvider>
      </FulfillmentProvider>
    </BranchProvider>
  );
}

/**
 * Listens for the API layer's request that the user re-authenticate (raised
 * after a 401 that token-refresh couldn't recover). Pops the sign-in modal
 * everywhere — once the user verifies, paused API calls resume automatically.
 */
function GlobalAuthPrompt() {
  const { signInRequired, cancelSignInPrompt } = useAuth();
  const { isMenuOnly } = useAppMode();
  // A menu-only link has no accounts, so a 401 has nothing to recover to.
  if (isMenuOnly) return null;
  return (
    <AuthFlow open={signInRequired} onClose={cancelSignInPrompt} />
  );
}

/**
 * Wires the foreground FCM listener once for the app session. While a tab is
 * focused FCM does not auto-display notifications, so we surface them ourselves
 * and broadcast order context for open screens to refresh. No-ops when web-push
 * isn't configured/supported.
 *
 * Also handles the "push-tap" deep-link for review pushes: when a foreground
 * order_review_request push arrives, jump straight to the review screen (same
 * aggressive intent as the in-app auto-prompt). Ordinary order pushes are left
 * alone here — TrackingScreen/OrderDetailScreen already react to
 * PUSH_ORDER_EVENT for those.
 */
function PushForegroundListener() {
  const navigate = useNavigate();
  useEffect(() => startForegroundPushListener(), []);
  useEffect(() => {
    function onReviewPush(e: Event) {
      const detail = (e as CustomEvent<{
        orderId: string | null;
        reviewUrl: string | null;
      }>).detail;
      // Prefer an in-app route: the app is already mounted on the right tenant
      // basename, so React Router keeps us on this origin and under this
      // tenant's slug. `reviewUrl` is an absolute URL the backend hardcodes to
      // menu.tappya.com, so assigning it would throw a staging or
      // custom-domain user onto prod (and re-bootstrap the whole app).
      if (detail?.orderId) {
        navigate(`/orders/${detail.orderId}/review`);
      } else if (detail?.reviewUrl) {
        window.location.assign(detail.reviewUrl);
      }
    }
    window.addEventListener(PUSH_REVIEW_EVENT, onReviewPush);
    return () => window.removeEventListener(PUSH_REVIEW_EVENT, onReviewPush);
  }, [navigate]);
  return null;
}

/**
 * App-level (screen-agnostic) review auto-prompt: fires on app open and on
 * app resume, so a physical counter pickup (app closed / on another screen
 * when the order flips to Completed) still gets prompted, unlike
 * OrderDetailScreen's on-screen-only auto-navigate. Renders a dismissible
 * modal rather than redirecting — the customer may have opened the app to
 * reorder, not to review.
 */
function ReviewPromptGate() {
  const { promptOrder, dismiss } = useReviewPromptTrigger();
  return <ReviewPromptModal order={promptOrder} onDismiss={dismiss} />;
}

function UnavailableRoute() {
  const { branchConfig } = useBranch();
  return (
    <MerchantUnavailable
      restaurantName={branchConfig ? storefrontName(branchConfig) : null}
      logoUrl={
        (branchConfig as { logoUrl?: string | null } | null)?.logoUrl ?? null
      }
    />
  );
}

/**
 * Persists dine-in table context in the URL across all navigations
 * by re-injecting the params if they are missing.
 */
function DineInParamsInjector() {
  const { tableInfo, isDineIn } = useTable();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isDineIn || !tableInfo) return;
    const params = new URLSearchParams(location.search);
    let changed = false;

    if (tableInfo.branchId && params.get("branchId") !== tableInfo.branchId) {
      params.set("branchId", tableInfo.branchId);
      changed = true;
    }
    if (tableInfo.tableId && params.get("tableId") !== tableInfo.tableId) {
      params.set("tableId", tableInfo.tableId);
      changed = true;
    }
    if (tableInfo.floorId && params.get("floorId") !== tableInfo.floorId) {
      params.set("floorId", tableInfo.floorId);
      changed = true;
    }

    if (changed) {
      navigate(location.pathname + "?" + params.toString(), { replace: true });
    }
  }, [isDineIn, tableInfo, location, navigate]);

  return null;
}

export function App() {
  // Slug resolution happens once per page load. On platform/staging/dev
  // domains the slug is the first path segment, and we hand it to React
  // Router as a `basename` so all child routes (`/cart`, `/checkout`, …)
  // render unchanged. On custom domains the slug comes from the hostname
  // and basename stays empty.
  const { basename } = resolveSlug();

  return (
    <AuthProvider>
      {/* Service-worker update prompt. Mounted once, above the router and
          outside BranchProvider: it depends on nothing but i18n, and must
          still appear on routes where branch config failed to resolve. */}
      <PwaUpdatePrompt />
      <TableProvider>
        <GroupCartProvider>
        <BrowserRouter basename={basename || undefined}>
          <Routes>
            {/* Public, unauthenticated location-pin page (SMS location-pin
                feature). Deliberately OUTSIDE BranchLayout: BranchProvider
                resolves a branch and this page has none — the customer opens
                it from an SMS link with no tenant/order context at all. It
                still sits under the router `basename` above, so the tenant
                slug survives in the URL (see the slug-less-link 401 bug
                class in bill-invite.ts) even though no branch is loaded. */}
            <Route path="pin/:token" element={<PinLocationScreen />} />
            {/* Public, unauthenticated live delivery tracking (delivery-program
                task). No tenant slug in the URL by design — see the header
                comment on PublicDeliveryTrackingScreen — so this sits outside
                BranchLayout exactly like pin/:token above. */}
            <Route path="track/:token" element={<PublicDeliveryTrackingScreen />} />
            <Route element={<BranchLayout />}>
              <Route path="/" element={<HomeScreen />} />
              <Route path="home" element={<HomeScreen />} />
              <Route path="item/:itemId" element={<ItemDetailScreen />} />
              <Route path="item-detail/:itemId" element={<ItemDetailScreen />} />
              <Route path="cart" element={<RequireOrdering><CartScreen /></RequireOrdering>} />
              <Route path="checkout" element={<RequireOrdering><CheckoutScreen /></RequireOrdering>} />
              <Route path="check-out" element={<RequireOrdering><CheckoutScreen /></RequireOrdering>} />
              <Route path="success" element={<RequireOrdering><SuccessScreen /></RequireOrdering>} />
              <Route path="order-success/:orderId" element={<RequireOrdering><SuccessScreen /></RequireOrdering>} />
              <Route path="tracking/:orderNum" element={<RequireOrdering><TrackingScreen /></RequireOrdering>} />
              <Route path="order-tracking/:orderNum" element={<RequireOrdering><TrackingScreen /></RequireOrdering>} />
              <Route path="dine-in-order/:orderId" element={<RequireOrdering><DineInOrderScreen /></RequireOrdering>} />
              <Route path="group/join" element={<RequireOrdering><GroupJoinScreen /></RequireOrdering>} />
              <Route path="orders/:orderId/split" element={<RequireOrdering><SplitPayScreen /></RequireOrdering>} />
              <Route path="orders/:orderId/cash/:splitId" element={<RequireOrdering><CashFollowUpScreen /></RequireOrdering>} />
              <Route path="history" element={<RequireOrdering><HistoryScreen /></RequireOrdering>} />
              <Route path="order-history" element={<RequireOrdering><HistoryScreen /></RequireOrdering>} />
              <Route path="orders/:orderId" element={<RequireOrdering><OrderDetailScreen /></RequireOrdering>} />
              <Route path="orders/:orderId/review" element={<RequireOrdering><ReviewSubmitScreen /></RequireOrdering>} />
              <Route path="profile" element={<RequireOrdering><ProfileScreen /></RequireOrdering>} />
              {/* Web & app chat (R7). Inside RequireOrdering because a
                  digital-menu-only link has no customer account to attach a
                  conversation to. The entry point that leads here is gated
                  on the tenant's chat availability; this route is not — a
                  customer with an existing thread can still open it. */}
              <Route path="chat" element={<RequireOrdering><ChatScreen /></RequireOrdering>} />
              <Route path="user-profile" element={<RequireOrdering><ProfileScreen /></RequireOrdering>} />
              <Route path="profile/edit" element={<RequireOrdering><EditProfileScreen /></RequireOrdering>} />
              <Route path="preferences" element={<RequireOrdering><PreferenceCenterScreen /></RequireOrdering>} />
              <Route path="saved-addresses" element={<RequireOrdering><SavedAddressesScreen /></RequireOrdering>} />
              <Route path="addresses" element={<RequireOrdering><SavedAddressesScreen /></RequireOrdering>} />
              <Route path="my-addresses" element={<RequireOrdering><SavedAddressesScreen /></RequireOrdering>} />
              <Route path="addresses/new" element={<RequireOrdering><NewAddressScreen /></RequireOrdering>} />
              <Route path="new-address" element={<RequireOrdering><NewAddressScreen /></RequireOrdering>} />
              <Route path="new-address/:addressId" element={<RequireOrdering><NewAddressScreen /></RequireOrdering>} />
              <Route path="payments" element={<RequireOrdering><SavedPaymentsScreen /></RequireOrdering>} />
              <Route path="payments/new" element={<RequireOrdering><NewPaymentScreen /></RequireOrdering>} />
              <Route path="loyalty-wallet" element={<RequireOrdering><LoyaltyWalletScreen /></RequireOrdering>} />
              <Route path="challenges" element={<RequireOrdering><ChallengesScreen /></RequireOrdering>} />
              <Route path="badges" element={<RequireOrdering><BadgesScreen /></RequireOrdering>} />
              <Route path="referrals" element={<RequireOrdering><ReferralsScreen /></RequireOrdering>} />
              <Route path="wallet-pass" element={<RequireOrdering><WalletPassScreen /></RequireOrdering>} />
              <Route path="offers" element={<RequireOrdering><OffersInboxScreen /></RequireOrdering>} />
              <Route path="points-history" element={<RequireOrdering><PointsHistoryScreen /></RequireOrdering>} />
              <Route path="map-picker" element={<RequireOrdering><MapPickerScreen /></RequireOrdering>} />
              <Route path="specify-location" element={<RequireOrdering><MapPickerScreen /></RequireOrdering>} />
              {/* Menu-only counterpart to the cart. */}
              <Route
                path="shortlist"
                element={
                  <RequireMenuOnly>
                    <ShortlistScreen />
                  </RequireMenuOnly>
                }
              />
              <Route path="unavailable" element={<UnavailableRoute />} />
              <Route path="directory" element={<HomeScreen />} />
              <Route path="*" element={<HomeScreen />} />
            </Route>
          </Routes>
        </BrowserRouter>
        </GroupCartProvider>
      </TableProvider>
    </AuthProvider>
  );
}
